import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { apiClient, APIError } from '@/utils/api-client';

export interface DocumentTag {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  is_system: boolean;
  created_at: string;
  created_by: string;
  updated_at?: string;
}

interface DocumentTagResponse {
  status: string;
  data: DocumentTag;
}

interface DocumentTagListResponse {
  status: string;
  data: DocumentTag[];
  total_count: number;
}

export interface DocumentTagCreate {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

interface DocumentTagUpdate {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
}

// Get all document tags
export function useDocumentTags() {
  return useApiQuery<DocumentTagListResponse>(
    ['documentTags'],
    () => apiClient.get('/documents/tags'),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}

// Create document tag
export function useCreateDocumentTag() {
  return useApiMutation<DocumentTagResponse, APIError, DocumentTagCreate>(
    (data) => apiClient.post('/documents/tags', data),
    {
      mutationKey: ['createDocumentTag'],
      invalidateKeys: [['documentTags']],
      successMessage: 'Document tag created successfully',
    }
  );
}

// Update document tag
export function useUpdateDocumentTag() {
  return useApiMutation<
    DocumentTagResponse,
    APIError,
    { id: string; data: DocumentTagUpdate }
  >(({ id, data }) => apiClient.put(`/documents/tags/${id}`, data), {
    mutationKey: ['updateDocumentTag'],
    invalidateKeys: [['documentTags']],
    successMessage: 'Document tag updated successfully',
  });
}

// Delete document tag
export function useDeleteDocumentTag() {
  return useApiMutation<{ status: string; message: string }, APIError, string>(
    (id) => apiClient.delete(`/documents/tags/${id}`),
    {
      mutationKey: ['deleteDocumentTag'],
      invalidateKeys: [['documentTags']],
      successMessage: 'Document tag deleted successfully',
    }
  );
}

// Update document tags
export function useUpdateDocumentTags() {
  return useApiMutation<
    { status: string; message: string },
    APIError,
    { documentId: string; tags: string[] }
  >(
    ({ documentId, tags }) =>
      apiClient.put(`/documents/${documentId}/tags`, tags),
    {
      mutationKey: ['updateDocumentTags'],
      invalidateKeys: [['documents']],
      successMessage: 'Document tags updated successfully',
    }
  );
}
